import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Edit, Trash2, Save, X, FileText, Eye, Sparkles } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { seedDefaultPages } from '@/utils/seedPages';

interface Page {
  id: string;
  title: string;
  slug: string;
  content?: string;
  meta_title?: string;
  meta_description?: string;
  published: boolean;
  created_at: string;
  updated_at: string;
}

interface NewPage {
  title: string;
  slug: string;
  content: string;
  meta_title: string;
  meta_description: string;
  published: boolean;
}

const AdminPages = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddingPage, setIsAddingPage] = useState(false);
  const [editingPage, setEditingPage] = useState<Page | null>(null);
  const [newPage, setNewPage] = useState<NewPage>({
    title: '',
    slug: '',
    content: '',
    meta_title: '',
    meta_description: '',
    published: false,
  });
  const queryClient = useQueryClient();

  const { data: pages = [], isLoading, error } = useQuery({
    queryKey: ['admin-pages', searchTerm],
    queryFn: async () => {
      try {
        let query = (supabase as any)
          .from('pages')
          .select('*')
          .order('created_at', { ascending: false });

        if (searchTerm) {
          query = query.ilike('title', `%${searchTerm}%`);
        }

        const { data, error } = await query;
        if (error) throw error;
        return (data || []) as Page[];
      } catch (err) {
        console.error('Failed to fetch pages:', err);
        throw err;
      }
    },
    retry: 2,
    staleTime: 30000,
  });

  // Seed default pages mutation
  const seedPagesMutation = useMutation({
    mutationFn: seedDefaultPages,
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['admin-pages'] });
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
    },
    onError: (error: any) => {
      toast.error(`Failed to seed pages: ${error.message}`);
    },
  });

  // Create page mutation
  const createPageMutation = useMutation({
    mutationFn: async (page: NewPage) => {
      const { error } = await (supabase as any)
        .from('pages')
        .insert([page]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-pages'] });
      toast.success('Page created successfully');
      setIsAddingPage(false);
      setNewPage({
        title: '',
        slug: '',
        content: '',
        meta_title: '',
        meta_description: '',
        published: false,
      });
    },
    onError: (error: any) => {
      console.error('Create page error:', error);
      toast.error(`Failed to create page: ${error.message || 'Unknown error'}`);
    },
  });

  // Update page mutation
  const updatePageMutation = useMutation({
    mutationFn: async (page: Page) => {
      const { error } = await (supabase as any)
        .from('pages')
        .update({
          title: page.title,
          slug: page.slug,
          content: page.content,
          meta_title: page.meta_title,
          meta_description: page.meta_description,
          published: page.published,
        })
        .eq('id', page.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-pages'] });
      toast.success('Page updated successfully');
      setEditingPage(null);
    },
    onError: (error: any) => {
      console.error('Update page error:', error);
      toast.error(`Failed to update page: ${error.message || 'Unknown error'}`);
    },
  });

  // Delete page mutation
  const deletePageMutation = useMutation({
    mutationFn: async (pageId: string) => {
      const { error } = await (supabase as any)
        .from('pages')
        .delete()
        .eq('id', pageId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-pages'] });
      toast.success('Page deleted successfully');
    },
    onError: (error: any) => {
      console.error('Delete page error:', error);
      toast.error(`Failed to delete page: ${error.message || 'Unknown error'}`);
    },
  });

  const handleCreatePage = () => {
    if (!newPage.title || !newPage.slug) {
      toast.error('Title and slug are required');
      return;
    }
    createPageMutation.mutate(newPage);
  };

  const handleUpdatePage = () => {
    if (!editingPage?.title || !editingPage?.slug) {
      toast.error('Title and slug are required');
      return;
    }
    updatePageMutation.mutate(editingPage);
  };

  const handleDeletePage = (pageId: string) => {
    if (window.confirm('Are you sure you want to delete this page?')) {
      deletePageMutation.mutate(pageId);
    }
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <Card className="p-6 text-center">
          <h3 className="text-lg font-semibold text-red-600 mb-2">Error Loading Pages</h3>
          <p className="text-gray-600 mb-4">{error.message || 'Failed to load pages'}</p>
          <Button onClick={() => queryClient.invalidateQueries({ queryKey: ['admin-pages'] })}>
            Try Again
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Pages Management</h1>
          <p className="text-gray-600 mt-1">Create and manage your website pages</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <Button
            onClick={() => seedPagesMutation.mutate()}
            disabled={seedPagesMutation.isPending}
            variant="outline"
            className="flex-1 sm:flex-none"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {seedPagesMutation.isPending ? 'Creating...' : 'Create Default Pages'}
          </Button>
          <Dialog open={isAddingPage} onOpenChange={setIsAddingPage}>
            <DialogTrigger asChild>
              <Button className="flex-1 sm:flex-none bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                <Plus className="w-4 h-4 mr-2" />
                Add New Page
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Add New Page</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    value={newPage.title}
                    onChange={(e) => {
                      const title = e.target.value;
                      setNewPage(prev => ({ 
                        ...prev, 
                        title,
                        slug: generateSlug(title)
                      }));
                    }}
                    placeholder="Page title"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="slug">Slug *</Label>
                  <Input
                    id="slug"
                    value={newPage.slug}
                    onChange={(e) => setNewPage(prev => ({ ...prev, slug: e.target.value }))}
                    placeholder="page-slug"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="meta_title">Meta Title</Label>
                  <Input
                    id="meta_title"
                    value={newPage.meta_title}
                    onChange={(e) => setNewPage(prev => ({ ...prev, meta_title: e.target.value }))}
                    placeholder="SEO title"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="meta_description">Meta Description</Label>
                  <Input
                    id="meta_description"
                    value={newPage.meta_description}
                    onChange={(e) => setNewPage(prev => ({ ...prev, meta_description: e.target.value }))}
                    placeholder="SEO description"
                  />
                </div>
                <div className="md:col-span-2 space-y-2">
                  <Label htmlFor="content">Content</Label>
                  <Textarea
                    id="content"
                    value={newPage.content}
                    onChange={(e) => setNewPage(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="Page content (HTML supported)"
                    rows={6}
                  />
                </div>
                <div className="md:col-span-2 flex items-center space-x-2">
                  <Switch
                    id="published"
                    checked={newPage.published}
                    onCheckedChange={(checked) => setNewPage(prev => ({ ...prev, published: checked }))}
                  />
                  <Label htmlFor="published">Published</Label>
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                <Button 
                  onClick={handleCreatePage} 
                  disabled={createPageMutation.isPending}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {createPageMutation.isPending ? 'Creating...' : 'Create Page'}
                </Button>
                <Button variant="outline" onClick={() => setIsAddingPage(false)}>
                  Cancel
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Edit Page Dialog */}
      <Dialog open={!!editingPage} onOpenChange={() => setEditingPage(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Page</DialogTitle>
          </DialogHeader>
          {editingPage && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="edit-title">Title *</Label>
                <Input
                  id="edit-title"
                  value={editingPage.title}
                  onChange={(e) => setEditingPage(prev => prev ? { ...prev, title: e.target.value } : null)}
                  placeholder="Page title"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-slug">Slug *</Label>
                <Input
                  id="edit-slug"
                  value={editingPage.slug}
                  onChange={(e) => setEditingPage(prev => prev ? { ...prev, slug: e.target.value } : null)}
                  placeholder="page-slug"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-meta_title">Meta Title</Label>
                <Input
                  id="edit-meta_title"
                  value={editingPage.meta_title || ''}
                  onChange={(e) => setEditingPage(prev => prev ? { ...prev, meta_title: e.target.value } : null)}
                  placeholder="SEO title"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-meta_description">Meta Description</Label>
                <Input
                  id="edit-meta_description"
                  value={editingPage.meta_description || ''}
                  onChange={(e) => setEditingPage(prev => prev ? { ...prev, meta_description: e.target.value } : null)}
                  placeholder="SEO description"
                />
              </div>
              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="edit-content">Content</Label>
                <Textarea
                  id="edit-content"
                  value={editingPage.content || ''}
                  onChange={(e) => setEditingPage(prev => prev ? { ...prev, content: e.target.value } : null)}
                  placeholder="Page content (HTML supported)"
                  rows={6}
                />
              </div>
              <div className="md:col-span-2 flex items-center space-x-2">
                <Switch
                  id="edit-published"
                  checked={editingPage.published}
                  onCheckedChange={(checked) => setEditingPage(prev => prev ? { ...prev, published: checked } : null)}
                />
                <Label htmlFor="edit-published">Published</Label>
              </div>
            </div>
          )}
          <div className="flex gap-2 mt-6">
            <Button 
              onClick={handleUpdatePage} 
              disabled={updatePageMutation.isPending}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {updatePageMutation.isPending ? 'Updating...' : 'Update Page'}
            </Button>
            <Button variant="outline" onClick={() => setEditingPage(null)}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Search */}
      <Card className="p-4 sm:p-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search pages..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </Card>

      {/* Pages Table */}
      <Card>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Page</TableHead>
                <TableHead className="hidden md:table-cell">Status</TableHead>
                <TableHead className="hidden lg:table-cell">Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  </TableCell>
                </TableRow>
              ) : pages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-gray-500">
                    No pages found
                  </TableCell>
                </TableRow>
              ) : (
                pages.map((page) => (
                  <TableRow key={page.id}>
                    <TableCell>
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <FileText className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{page.title}</div>
                          <div className="text-sm text-gray-500">/{page.slug}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <Badge variant={page.published ? "default" : "secondary"}>
                        {page.published ? 'Published' : 'Draft'}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <div className="text-sm text-gray-600">
                        {formatDate(page.created_at)}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Button size="sm" variant="ghost" className="hidden sm:inline-flex">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => setEditingPage(page)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeletePage(page.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
};

export default AdminPages;
